// Single source of truth for the /programs index page.
// Every entry resolves to an existing internal ProgramTemplate route
// (slug matches the key in programSlugs.js).
//
// `titleLabel` (≤ 25 chars) is the SEO/H1 label used by combo pages
// (/programs/<slug>/<city>) — kept short so the pattern
// "<titleLabel> Training in <City> | Anderson"  stays ≤ 60 chars for
// every campus and never gets mid-word-truncated in Google SERPs.
// Do NOT put "Diploma" or parenthetical qualifiers in titleLabel.

export const PROGRAMS_CATALOG = [
  { slug: "medical-office-administrator",
    name: "Medical Office Administrator (In-Campus)",
    titleLabel: "Medical Office Admin",
    tagline: "CCHIM-accredited diploma with an 8-week practicum — write the National Certification Exam (NCE) and qualify as a HICA.",
    delivery: "On-Campus", durationWeeks: 48, industry: "Healthcare",
    heroImage: "/assets/programs/moa/hero-Background-2.png",
    pills: ["CCHIM-Accredited", "8-Week Practicum"] },

  { slug: "medical-office-administrator-online",
    name: "Medical Office Administrator (Online)",
    titleLabel: "Medical Office Admin",
    tagline: "100% online MOA training — same in-demand admin skills, capstone project in place of practicum.",
    delivery: "Online", durationWeeks: 44, industry: "Healthcare",
    heroImage: "/assets/programs/moa/hero-Background-2.png",
    pills: ["100% Online", "Capstone Project"] },

  { slug: "personal-support-worker",
    name: "Personal Support Worker",
    titleLabel: "Personal Support Worker",
    tagline: "Hybrid 27-week path into hospitals, long-term care and home settings.",
    delivery: "Hybrid", durationWeeks: 27, industry: "Healthcare",
    heroImage: "/assets/programs/psw/hero-Background-18.jpg",
    pills: ["NACC-Aligned", "Clinical Placement"] },

  { slug: "business-administration",
    name: "Business Administration",
    titleLabel: "Business Administration",
    tagline: "Finance, marketing, operations & admin in one fast-tracked diploma.",
    delivery: "Online", durationWeeks: 42, industry: "Business",
    heroImage: "/assets/programs/batch2/hero-bg4.jpg",
    pills: ["Fast-Track", "Career Services"] },

  { slug: "medical-laboratory-assistant",
    name: "Medical Laboratory Assistant Technician",
    titleLabel: "Medical Lab Assistant",
    tagline: "Specimen collection, phlebotomy and lab processing skills.",
    delivery: "On-Campus", durationWeeks: 50, industry: "Healthcare",
    heroImage: "/assets/programs/mla/hero-medical-workers.webp",
    pills: ["CSMLS-Prep", "Clinical Placement"] },

  { slug: "accounting-and-payroll-administration",
    name: "Accounting & Payroll Administrator Diploma",
    titleLabel: "Accounting & Payroll",
    tagline: "Fast-track your accounting and payroll career with our nationally recognized online program and hands-on software training.",
    delivery: "Online", durationWeeks: 49, industry: "Business",
    heroImage: "/assets/programs/batch2/hero-apa.jpg",
    pills: ["PCP Eligible", "QuickBooks & Sage"] },

  { slug: "cardiology-technologist",
    name: "Cardiology Technology Diploma",
    titleLabel: "Cardiology Technology",
    tagline: "Launch your cardiology technologist career with expert training, hands-on clinical experience, and CSCT exam preparation.",
    delivery: "On-Campus", durationWeeks: 76, industry: "Healthcare",
    heroImage: "/assets/programs/batch2/hero-cardiology.jpg",
    pills: ["EQual-accredited", "600 Practicum Hours"] },

  { slug: "intra-oral-dental-assistant",
    name: "Intra Oral Level I & II Dental Assistant Diploma",
    titleLabel: "Dental Assistant",
    tagline: "Launch your dental assistant career with expert training, hands-on practice, and real-world skills.",
    delivery: "On-Campus", durationWeeks: 51, industry: "Healthcare",
    heroImage: "/assets/programs/batch2/hero-dental.jpg",
    pills: ["NDAEB Recognized", "Two Practicums"] },

  { slug: "pharmacy-technician",
    name: "Pharmacy Technician Diploma",
    titleLabel: "Pharmacy Technician",
    tagline: "Prepare for licensed pharmacy technician roles supporting safe medication delivery and patient care.",
    // 2026-02 structured-data cleanup: `durationWeeks` was 27 (older
    // spec that counted only the hybrid classroom portion). Visible
    // AtAGlance fact is "64 Weeks + 360 Practicum Hours" — the full
    // end-to-end program. Updated so Course `timeRequired` matches.
    delivery: "Hybrid", durationWeeks: 64, industry: "Healthcare",
    placementHours: 360,
    heroImage: "/assets/programs/batch2/hero-pharmacy.jpg",
    pills: ["CCAPP-accredited", "360h Practicum"] },

  { slug: "paralegal",
    name: "Paralegal Diploma",
    titleLabel: "Paralegal",
    tagline: "Launch your paralegal career through accredited training, advocacy skills, and licensing exam preparation.",
    delivery: "On-Campus", durationWeeks: 50, industry: "Law",
    // 2026-02 structured-data cleanup: explicit classroom/placement
    // hours so Course JSON-LD's `courseWorkload` matches the visible
    // AtAGlance fact ("50 Weeks (1,120 Hours) + 240-Hour Placement").
    // Placement runs concurrent with the 50 weeks per registrar;
    // `timeRequired` stays P50W.
    classroomHours: 1120, placementHours: 240,
    heroImage: "/assets/programs/batch2/hero-paralegal.jpg",
    pills: ["LSO Accredited", "240h Placement"] },

  { slug: "supply-chain-logistics",
    name: "Supply Chain and Logistics Diploma",
    titleLabel: "Supply Chain & Logistics",
    tagline: "Launch your supply chain career with expertise in purchasing, transportation, warehousing, and global trade.",
    delivery: "Online", durationWeeks: 46, industry: "Business",
    heroImage: "/assets/programs/batch2/hero-supply.jpg",
    pills: ["Eligible for CIFFA", "100% Online"] },

  { slug: "health-information-management",
    name: "Health Information Management Diploma",
    titleLabel: "Health Info Management",
    tagline: "Prepare for in-demand healthcare roles managing patient data, digital systems, and health information governance.",
    delivery: "Online", durationWeeks: 60, industry: "Healthcare",
    heroImage: "/assets/programs/batch2/hero-him.jpg",
    pills: ["CCHIM Accredited", "100% Online"] },

  { slug: "information-technology-technician",
    name: "Information Technology Technician Diploma",
    titleLabel: "IT Technician",
    tagline: "Prepare for IT Technician roles with hands-on training in networking, cloud, cybersecurity, and certifications.",
    delivery: "Online", durationWeeks: 36, industry: "Technology",
    heroImage: "/assets/programs/batch2/hero-it.jpg",
    pills: ["Fast-Track", "CompTIA Path"] },

  { slug: "computer-system-technician-networking",
    name: "Computer System Technician – Networking Diploma",
    titleLabel: "CST – Networking",
    tagline: "Master hardware, servers, networking, cloud, cybersecurity, and automation to launch a career in computer systems and network support.",
    delivery: "Online", durationWeeks: 62, industry: "Technology",
    heroImage: "/assets/programs/cstn/hero.webp",
    pills: ["62 Weeks", "CompTIA + Cisco Path"] },

  { slug: "early-childhood-education",
    name: "Early Childhood Education Diploma",
    titleLabel: "Early Childhood Ed",
    tagline: "Launch your Early Childhood Education journey with classroom learning and three practicums, preparing you for rewarding careers and ECE registration.",
    delivery: "On-Campus", durationWeeks: 75, industry: "Childcare",
    heroImage: "/assets/programs/batch2/hero-ece.jpg",
    pills: ["Approved by CECE", "3 Practicums"] },

  { slug: "medical-esthetician",
    name: "Medical Esthetician Diploma",
    titleLabel: "Medical Esthetician",
    tagline: "Develop medical esthetics skills through expert-led training, simulated spa learning, and practicum experience.",
    delivery: "On-Campus", durationWeeks: 66, industry: "Healthcare",
    heroImage: "/assets/programs/batch2/hero-esthetician.jpg",
    pills: ["Laser & IPL", "4-Week Practicum"] },

  { slug: "pharmacy-assistant",
    name: "Pharmacy Assistant Diploma – Hybrid",
    titleLabel: "Pharmacy Assistant",
    tagline: "Launch your pharmacy assistant career through expert training, hands-on practicum, and real clinical placement.",
    delivery: "Hybrid", durationWeeks: 34, industry: "Healthcare",
    // Visible AtAGlance: "34 Weeks (476 Online + 84 In-Class) +
    // 225-Hour Practicum" → classroom = 560, placement = 225.
    classroomHours: 560, placementHours: 225,
    heroImage: "/assets/programs/batch2/hero-pharmasst.jpg",
    pills: ["225h Practicum", "Hybrid"] },

  { slug: "diagnostic-medical-sonographer",
    name: "Diagnostic Medical Sonographer Diploma",
    titleLabel: "Medical Sonographer",
    tagline: "Launch your sonographer career through accredited practical training, expert guidance, and CMRITO exam prep.",
    delivery: "On-Campus", durationWeeks: 104, industry: "Healthcare",
    heroImage: "/assets/programs/batch2/hero-dms.jpg",
    pills: ["EQual-Accredited", "Two 16-wk Practicums"] },

  { slug: "orthopaedic-technician",
    name: "Orthopaedic Technician Diploma",
    titleLabel: "Orthopaedic Technician",
    tagline: "Fast-track your orthopaedics career with labs, clinical placements, and hands-on training in casting, splinting, and musculoskeletal assessment.",
    delivery: "On-Campus", durationWeeks: 47, industry: "Healthcare",
    heroImage: "/assets/programs/batch2/hero-ortho.jpg",
    pills: ["330h Practicum", "Fast-Track"] },

  // Orthopaedic Technician reinstated 2026-02-15 per business request — full page, forms, and menu links restored.
  // Physiotherapist & OT Assistant hidden 2026-08-25 per business request — redirected to Medical Laboratory Assistant.

  // Dental Radiography hidden 2026-06 per business request — kept here as a comment for traceability.
  // { slug: "dental-radiography", name: "Dental Radiography – H.A.R.P. Approved", ... }

  { slug: "law-enforcement-police-foundations",
    name: "Law Enforcement and Police Foundations Diploma",
    titleLabel: "Police Foundations",
    tagline: "Prepare for law enforcement, security, and investigative careers with police-academy readiness and hands-on practicum.",
    delivery: "Hybrid", durationWeeks: 42, industry: "Law",
    heroImage: "/assets/programs/batch2/hero-law.jpg",
    pills: ["Police Academy Ready", "4-Week Practicum"] },

  { slug: "occupational-health-and-safety",
    name: "Occupational Health and Safety Diploma",
    titleLabel: "Occ. Health & Safety",
    tagline: "Develop OHS expertise in hazard prevention, risk management, ergonomics, and industrial safety, and prepare for certification-ready career pathways.",
    delivery: "Online", durationWeeks: 47, industry: "Business",
    heroImage: "/assets/programs/batch2/hero-ohs.jpg",
    pills: ["Approved by BCRSP", "100% Online"] },

  { slug: "early-childcare-assistant",
    name: "Early Childcare Assistant Diploma",
    titleLabel: "Childcare Assistant",
    tagline: "Build your childhood assistant career through training in child development, health, safety, inclusive play-based learning, and immersive practicums.",
    // 2026-02 structured-data cleanup: delivery was "Online" but the
    // visible AtAGlance fact says "Hybrid (Online + In-Person
    // Practicums)" — program ships 675 online classroom hours plus
    // 325 practicum hours. Corrected to Hybrid; visible pill updated
    // separately on the ecaData page.
    delivery: "Hybrid", durationWeeks: 40, industry: "Childcare",
    classroomHours: 675, placementHours: 325,
    heroImage: "/assets/programs/batch2/hero-eca.jpg",
    pills: ["NACC Curriculum", "Hybrid"] },

  // Child & Youth Care with Addictions Support Worker hidden 2026-08-25 per business request — redirected to Personal Support Worker.

  // "Medical Laboratory Technician" entry removed 2026-02 (item 6) — see
  // ProgramRoute.jsx for the legacy URL redirect to MLAT.
  { slug: "medical-laboratory-technology",
    name: "Medical Laboratory Technology Diploma",
    titleLabel: "Medical Lab Technology",
    tagline: "Prepare for Medical Laboratory Technologist career through EQual-accredited training, advanced lab skills, preparing graduates for CAMLPR exam and CMLTO registration.",
    delivery: "Hybrid", durationWeeks: 104, industry: "Healthcare",
    heroImage: "/assets/programs/batch2/hero-mlt.jpg",
    pills: ["EQual™ Accredited", "940h Practicum"] },
];

export const DELIVERY_OPTIONS = ["All", "Online", "On-Campus", "Hybrid"];
export const INDUSTRY_OPTIONS = ["All", "Healthcare", "Business", "Technology", "Childcare", "Law"];
export const DURATION_OPTIONS = [
  { key: "any",   label: "Any",            match: () => true },
  { key: "short", label: "< 6 months",     match: (w) => w < 26 },
  { key: "mid",   label: "6–12 months",    match: (w) => w >= 26 && w <= 52 },
  { key: "long",  label: "1–2 years",      match: (w) => w > 52 },
];

export const countBy = (list, key, value) =>
  value === "All" ? list.length : list.filter((p) => p[key] === value).length;

// ─────────────────────────────────────────────────────────────────────
// Machine-readable metadata for SEO JSON-LD
// (used by seoData.js → EducationalOccupationalProgram schema, and by
// scripts/refresh_llms.js → llms-full.txt one-liners)
// ─────────────────────────────────────────────────────────────────────
//
// PROGRAM_CREDENTIAL — every current Anderson program is a Diploma per
//   the catalog `name`/`tagline` copy. If a future certificate program
//   is added, override per-slug here.
export const PROGRAM_CREDENTIAL = "Diploma";

// PROGRAM_NOC_MAP — slug → { code, label } for the NOC 2021 occupation
//   that a graduate is qualified for. `label` MUST be the official
//   Statistics Canada NOC 2021 title (verified against
//   https://noc.esdc.gc.ca/). Do NOT invent labels; do NOT source codes
//   from backend/config/job_banner_config.py — that file holds job-bank
//   display labels, not the canonical NOC 2021 crosswalk used for the
//   per-program `noc:` fields in the individual programme data files.
//
//   Every one of the 24 catalog programs has a mapping. If a program is
//   ever added to PROGRAMS_CATALOG, add its slug here in the same commit.
export const PROGRAM_NOC_MAP = {
  "personal-support-worker":                          { code: "44101", label: "Home support workers, caregivers and related occupations" },
  "medical-office-administrator":                     { code: "13112", label: "Medical administrative assistants" },
  "medical-office-administrator-online":              { code: "13112", label: "Medical administrative assistants" },
  "medical-laboratory-assistant":                     { code: "33101", label: "Medical laboratory assistants and related technical occupations" },
  "medical-laboratory-technology":                    { code: "32120", label: "Medical laboratory technologists" },
  "business-administration":                          { code: "13100", label: "Administrative officers" },
  "accounting-and-payroll-administration":            { code: "13102", label: "Payroll administrators" },
  "cardiology-technologist":                          { code: "32123", label: "Cardiology technologists and electrophysiological diagnostic technologists" },
  "intra-oral-dental-assistant":                      { code: "33100", label: "Dental assistants and dental laboratory assistants" },
  "pharmacy-technician":                              { code: "32124", label: "Pharmacy technicians" },
  "pharmacy-assistant":                               { code: "33102", label: "Pharmacy technical assistants and pharmacy assistants" },
  "diagnostic-medical-sonographer":                   { code: "32122", label: "Medical sonographers" },
  "orthopaedic-technician":                           { code: "33109", label: "Other assisting occupations in support of health services" },
  "physiotherapist-occupational-therapist-assistant": { code: "32109", label: "Other technical occupations in therapy and assessment" },
  "medical-esthetician":                              { code: "63211", label: "Estheticians, electrologists and related occupations" },
  "paralegal":                                        { code: "42200", label: "Paralegals and related occupations" },
  "supply-chain-logistics":                           { code: "13201", label: "Production and transportation logistics coordinators" },
  "health-information-management":                    { code: "12111", label: "Health information management occupations" },
  "information-technology-technician":                { code: "22220", label: "Computer network and web technicians" },
  "computer-system-technician-networking":            { code: "22220", label: "Computer network and web technicians" },
  "early-childhood-education":                        { code: "42202", label: "Early childhood educators and assistants" },
  "early-childcare-assistant":                        { code: "42202", label: "Early childhood educators and assistants" },
  "child-youth-care-addictions-support-worker":       { code: "42201", label: "Social and community service workers" },
  "law-enforcement-police-foundations":               { code: "64410", label: "Security guards and related security service occupations" },
  "occupational-health-and-safety":                   { code: "22232", label: "Occupational health and safety specialists" },
};
